/**
 * @file filters.tsx
 *
 * @fileoverview Renders facets in the form of tabs. Can be used for example inside a
 * bottom drawer.
 */

import React from 'react';
import FacetMultiple from './../facet-multiple';
import { Facet } from '~/jobiqo/models/search';
import FacetSingle from './../facet-single';
import { useIntl } from 'react-intl';
import { Tab, TabList, TabPanel, Tabs } from '~/vcl/atoms/17-tabs';
import { Badge } from '~/vcl/atoms/03-badge';

export interface FacetTabsProps {
  querystring: any;
  pathname: string;
  facets: Facet[];
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  getFacetSelectedItems(facet: Facet): string[];
  renderAdditionalTabs?: () => React.ReactNode;
  renderAdditionalTabPanels?: () => React.ReactNode;
}

export const FacetTabs = ({
  facets,
  querystring,
  pathname,
  onFacetToggle,
  getFacetSelectedItems,
  renderAdditionalTabs,
  renderAdditionalTabPanels,
}: FacetTabsProps) => {
  const intl = useIntl();
  return (
    <Tabs
      defaultValue={facets[0].name}
      tabsListItemFull={true}
      tabEqualSpace={false}
    >
      <TabList>
        {facets.map((facet) => {
          return (
            <Tab key={facet.name} value={facet.name}>
              <>
                {!!intl.messages &&
                intl.messages?.[`config_facet_${facet.name}_title`]
                  ? intl.messages[`config_facet_${facet.name}_title`]
                  : facet.title}
                {getFacetSelectedItems(facet).length ? (
                  <>
                    &nbsp;&nbsp;
                    <Badge badgeStyle="default">
                      {getFacetSelectedItems(facet).length}
                    </Badge>
                  </>
                ) : (
                  ''
                )}
              </>
            </Tab>
          );
        })}
        {renderAdditionalTabs && renderAdditionalTabs()}
      </TabList>
      {facets.map((facet) => {
        return (
          <TabPanel key={facet.name} value={facet.name}>
            {facet.multiple ? (
              <FacetMultiple
                querystring={querystring}
                onFacetToggle={(facetItem, selectedItems, alias) =>
                  onFacetToggle(facetItem, selectedItems, alias)
                }
                selectedItems={getFacetSelectedItems(facet)}
                facet={facet}
              />
            ) : (
              <FacetSingle
                querystring={querystring}
                pathname={pathname}
                selectedItems={getFacetSelectedItems(facet)}
                facet={facet}
                onFacetToggle={(facetItem, selectedItems, alias) =>
                  onFacetToggle(facetItem, selectedItems, alias)
                }
              />
            )}
          </TabPanel>
        );
      })}
      {renderAdditionalTabPanels && renderAdditionalTabPanels()}
    </Tabs>
  );
};
